/*
 * ELASTICSEARCH CONFIDENTIAL
 * __________________
 *
 *  Copyright Elasticsearch B.V. All rights reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Elasticsearch B.V. and its suppliers, if any.
 * The intellectual and technical concepts contained herein
 * are proprietary to Elasticsearch B.V. and its suppliers and
 * may be covered by U.S. and Foreign Patents, patents in
 * process, and are protected by trade secret or copyright
 * law.  Dissemination of this information or reproduction of
 * this material is strictly forbidden unless prior written
 * permission is obtained from Elasticsearch B.V.
 */
import React, { type FC } from 'react'
import { defineMessages, useIntl } from 'react-intl'

import { EuiFlexGroup, EuiFlexItem, EuiFormRow, EuiSelect } from '@elastic/eui'

import messages, { roleMessages } from './messages'
import { CCM_ROLES } from './constants'

type RoleSelectOptionValue = (typeof CCM_ROLE_SELECTOR_OPTIONS)[number]

type CCMRoleDropdownProps = {
  onRoleChange: (value: RoleSelectOptionValue) => void
}

const CCM_ROLE_SELECTOR_OPTIONS = ['none', ...CCM_ROLES] as const

const globalCCMRoleSelectorMessages = defineMessages<RoleSelectOptionValue>({
  none: {
    id: 'roles-assignments.ccm-access.role-placeholder',
    defaultMessage: 'None',
  },
  ...roleMessages,
})

export const CCMRoleDropdown: FC<CCMRoleDropdownProps> = ({ onRoleChange }) => {
  const { formatMessage } = useIntl()

  const ccmRoleSelectionOptions: Array<{ value: RoleSelectOptionValue; text: string }> =
    CCM_ROLE_SELECTOR_OPTIONS.map((role) => ({
      value: role,
      text: formatMessage(globalCCMRoleSelectorMessages[role]),
    }))

  return (
    <EuiFlexGroup direction='column' gutterSize='s'>
      <EuiFlexItem>
        <EuiFormRow
          label={formatMessage(messages.allConnectedClustersRole)}
          fullWidth={true}
          helpText={formatMessage(messages.allConnectedClustersRoleHelp)}
        >
          <EuiSelect
            options={ccmRoleSelectionOptions}
            onChange={(evt) => {
              onRoleChange(evt.target.value as RoleSelectOptionValue)
            }}
            aria-label={formatMessage(messages.globalRoleDropdownAccessibleLabel)}
            data-test-id='ccm-role-dropdown'
          />
        </EuiFormRow>
      </EuiFlexItem>
    </EuiFlexGroup>
  )
}
